import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import {
  TEMPERATURE_SENSOR_MODELS,
  SENSOR_TYPE_LABELS,
  calculateSensorResistance,
  calculateSensorTemperature,
  formatResistanceValue,
  getSensorResistanceRange,
  getSensorModelById,
  type SensorFormulaType,
  type TemperatureSensorModel
} from '../../utils/temperatureSensors';

const DEFAULT_SENSOR_IDS = TEMPERATURE_SENSOR_MODELS.slice(0, 2).map((model) => model.id);
const SENSOR_TYPE_ORDER: SensorFormulaType[] = ['pt', 'ni_din', 'ni_tk5000', 'ntc'];
type CalculationDirection = 'temp_to_res' | 'res_to_temp';

const normalizeNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatTemperature = (value: number): string =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
    maximumFractionDigits: 1
  }).format(value);

const SENSOR_GROUPS = SENSOR_TYPE_ORDER.map((type) => ({
  type,
  label: SENSOR_TYPE_LABELS[type],
  models: TEMPERATURE_SENSOR_MODELS.filter((model) => model.type === type)
}));

const FORMULA_TEXT_BY_TYPE: Record<SensorFormulaType, string> = {
  pt: 'PT (IEC 60751): Callendar-van-Dusen Kennlinie (positiver/negativer Bereich).',
  ni_din: 'NI DIN: quadratische Kennlinie nach DIN-Naeherung mit R0 bei 0 °C.',
  ni_tk5000: 'NI TK5000: lineare Kennlinie mit Temperaturkoeffizient 5000 ppm/K.',
  ntc: 'NTC: Beta-Modell mit R25 = Nennwiderstand (B-Wert je Typ).'
};

type ComparisonItem = {
  modelId: string;
  label: string;
  state: 'ok' | 'error';
  valueText: string;
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewTemperaturen } = useAuth();
  const [sensorIds, setSensorIds] = useState<string[]>(DEFAULT_SENSOR_IDS);
  const [direction, setDirection] = useState<CalculationDirection>('temp_to_res');
  const [temperatureInput, setTemperatureInput] = useState<string>('20');
  const [resistanceInput, setResistanceInput] = useState<string>('100');

  const selectedModels = useMemo<TemperatureSensorModel[]>(
    () =>
      sensorIds
        .map((id) => getSensorModelById(id))
        .filter((model): model is TemperatureSensorModel => Boolean(model)),
    [sensorIds]
  );

  const temperatureValue = useMemo(() => normalizeNumber(temperatureInput), [temperatureInput]);
  const resistanceValue = useMemo(() => normalizeNumber(resistanceInput), [resistanceInput]);
  const temperatureRangeText = useMemo(() => {
    if (selectedModels.length === 0) return '';
    const minTemp = Math.max(...selectedModels.map((model) => model.minTempC));
    const maxTemp = Math.min(...selectedModels.map((model) => model.maxTempC));
    if (minTemp <= maxTemp) {
      return `Gemeinsamer Bereich: ${formatTemperature(minTemp)} bis ${formatTemperature(maxTemp)} °C`;
    }
    return 'Kein gemeinsamer Temperaturbereich fuer alle ausgewaehlten Fuehler.';
  }, [selectedModels]);

  const resistanceRangeText = useMemo(() => {
    if (selectedModels.length === 0) return '';
    const ranges = selectedModels
      .map((model) => getSensorResistanceRange(model.id))
      .filter((range): range is NonNullable<typeof range> => Boolean(range));

    if (ranges.length !== selectedModels.length) {
      return 'Widerstandsbereich fuer einen oder mehrere Fuehler nicht verfuegbar.';
    }

    const minResistance = Math.max(...ranges.map((range) => range.minResistanceOhm));
    const maxResistance = Math.min(...ranges.map((range) => range.maxResistanceOhm));

    if (minResistance <= maxResistance) {
      return `Gemeinsamer Bereich: ${formatResistanceValue(minResistance)} bis ${formatResistanceValue(maxResistance)}`;
    }
    return 'Kein gemeinsamer Widerstandsbereich fuer alle ausgewaehlten Fuehler.';
  }, [selectedModels]);

  const selectedModelNames = useMemo(
    () =>
      sensorIds
        .map((id) => getSensorModelById(id)?.label)
        .filter((label): label is string => Boolean(label)),
    [sensorIds]
  );

  const result = useMemo(() => {
    if (selectedModels.length === 0) {
      return { state: 'empty' as const, message: 'Bitte mindestens einen Fuehlertyp waehlen.' };
    }

    if (direction === 'temp_to_res') {
      if (temperatureValue === null) return { state: 'empty' as const, message: 'Bitte Temperatur eingeben.' };
      const items: ComparisonItem[] = selectedModels.map((model) => {
        const calculated = calculateSensorResistance(model.id, temperatureValue);
        if (!calculated.ok) {
          return {
            modelId: model.id,
            label: model.label,
            state: 'error',
            valueText: calculated.error
          };
        }

        return {
          modelId: model.id,
          label: model.label,
          state: 'ok',
          valueText: formatResistanceValue(calculated.resistanceOhm)
        };
      });

      return {
        state: 'ok' as const,
        mode: 'temp_to_res' as const,
        items
      };
    }

    if (resistanceValue === null) return { state: 'empty' as const, message: 'Bitte Widerstand eingeben.' };
    const items: ComparisonItem[] = selectedModels.map((model) => {
      const calculated = calculateSensorTemperature(model.id, resistanceValue);
      if (!calculated.ok) {
        return {
          modelId: model.id,
          label: model.label,
          state: 'error',
          valueText: calculated.error
        };
      }

      return {
        modelId: model.id,
        label: model.label,
        state: 'ok',
        valueText: `${formatTemperature(calculated.temperatureC)} °C`
      };
    });

    return {
      state: 'ok' as const,
      mode: 'res_to_temp' as const,
      items
    };
  }, [selectedModels, direction, temperatureValue, resistanceValue]);

  const formulaText = useMemo(() => {
    if (selectedModels.length === 0) return 'Bitte Fuehlertypen waehlen.';
    const selectedTypes = Array.from(new Set(selectedModels.map((model) => model.type)));
    return selectedTypes.map((type) => FORMULA_TEXT_BY_TYPE[type]).join(' ');
  }, [selectedModels]);

  if (!canViewTemperaturen) {
    return (
      <UnitPageShell title="M99 U4 - Temperaturen">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 U4 - Temperaturen">
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' } }}>
        <Card sx={getCardSx(darkMode)}>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={700}>Passiver Fuehler</Typography>
                <Typography variant="body2" color="text.secondary">
                  Vergleich von 2 oder mehr passiven Fuehlern mit gemeinsamer Vorgabe (Temperatur oder Widerstand).
                </Typography>
              </Stack>

              <ToggleButtonGroup
                value={direction}
                exclusive
                onChange={(_, value: CalculationDirection | null) => {
                  if (value) setDirection(value);
                }}
                size="small"
              >
                <ToggleButton value="temp_to_res">Temperatur → Widerstand</ToggleButton>
                <ToggleButton value="res_to_temp">Widerstand → Temperatur</ToggleButton>
              </ToggleButtonGroup>

              <FormControl fullWidth>
                <InputLabel id="sensor-select-label">Fuehlertypen</InputLabel>
                <Select<string[]>
                  labelId="sensor-select-label"
                  label="Fuehlertypen"
                  multiple
                  value={sensorIds}
                  onChange={(event: SelectChangeEvent<string[]>) => {
                    const value = event.target.value;
                    setSensorIds(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) =>
                    selected
                      .map((id) => getSensorModelById(id)?.label ?? id)
                      .join(' · ')
                  }
                >
                  {SENSOR_GROUPS.map((group) => [
                    <ListSubheader key={`group-${group.type}`}>{group.label}</ListSubheader>,
                    ...group.models.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        <Checkbox size="small" checked={sensorIds.includes(model.id)} />
                        <ListItemText primary={model.label} />
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={direction === 'temp_to_res' ? 'Temperatur (°C)' : 'Widerstand (Ω)'}
                value={direction === 'temp_to_res' ? temperatureInput : resistanceInput}
                onChange={(event) => {
                  if (direction === 'temp_to_res') {
                    setTemperatureInput(event.target.value);
                  } else {
                    setResistanceInput(event.target.value);
                  }
                }}
                helperText={
                  direction === 'temp_to_res' ? temperatureRangeText : resistanceRangeText
                }
              />

              <Typography variant="caption" color="text.secondary">
                Ausgewaehlt: {selectedModelNames.length > 0 ? selectedModelNames.join(' · ') : '-'}
              </Typography>

              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                  border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ergebnis
                  </Typography>
                  {result.state === 'ok' ? (
                    <Stack spacing={1}>
                      {result.items.map((item) => (
                        <Stack key={item.modelId} spacing={0.25}>
                          <Typography variant="caption" color="text.secondary">
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={item.state === 'ok' ? tokens.colors.primary : 'text.secondary'}
                          >
                            {item.valueText}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {result.message}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {result.state === 'ok' && result.items.some((item) => item.state === 'error') && (
                <Alert severity="warning">Mindestens ein Fuehlertyp liegt ausserhalb des gueltigen Bereichs.</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={2.5}>
          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Kennlinie
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formulaText}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                Hinweis: Typen mit * entsprechen den Bezeichnungen aus der bestehenden Toolliste.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Verfuegbare Fuehler
              </Typography>
              <Stack spacing={1.5}>
                {SENSOR_GROUPS.map((group) => (
                  <Stack key={group.type} spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">{group.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.models.map((model) => model.label).join(' · ')}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
